import PersonAddAltIcon from "@mui/icons-material/PersonAddAltRounded";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import useClientContext from "../../../../context/useClientContext";
import NavigateByLink from "../../../common/NavigateByLink";
import type { CommunicationMatrixModel } from "./matrixHelper";
import { emptyGuid, investorGroupingKeySeparator } from "./matrixHelper";
import RelatedCommMatrixRecordLink from "./RelatedCommMatrixRecordLink";

interface FundMatrixInvestorGroupCellProps {
  value: string;
  groupingKey: string | undefined;
  investorId: string | undefined;
  onContactAdd: () => void;
}

export const FundMatrixInvestorGroupCell = ({
  value,
  groupingKey,
  investorId,
  onContactAdd,
}: FundMatrixInvestorGroupCellProps) => {
  const { hasAnyPermission } = useClientContext();
  const hasAccessToManage = hasAnyPermission(["ManageFundStructure"]);

  const investorName = value || groupingKey || "";
  const namePaddingLeft = value ? 0 : 5;

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {investorId ? (
        <RelatedCommMatrixRecordLink
          recordType="Investor"
          recordId={investorId}
          text={investorName}
          sx={{ pl: namePaddingLeft }}
          typographyProps={{ variant: "subtitle1", sx: { maxWidth: "11rem" } }}
        />
      ) : (
        <TypographyTooltipEllipsis
          text={investorName}
          typographyProps={{ variant: "subtitle1", sx: { pl: namePaddingLeft, maxWidth: "11rem" } }}
        />
      )}
      {hasAccessToManage && (
        <Tooltip title="Add Contact" arrow>
          <IconButton onClick={onContactAdd}>
            <PersonAddAltIcon />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

interface ContactMatrixInvestorGroupCellProps {
  value: string;
  investorId: string | undefined;
}

export const ContactMatrixInvestorGroupCell = ({ value, investorId }: ContactMatrixInvestorGroupCellProps) =>
  investorId ? (
    <RelatedCommMatrixRecordLink
      recordType="Investor"
      recordId={investorId}
      text={value}
      typographyProps={{ variant: "subtitle1", sx: { maxWidth: "11.5rem" } }}
    />
  ) : (
    <TypographyTooltipEllipsis
      key={value}
      text={value}
      typographyProps={{ variant: "subtitle1", sx: { maxWidth: "11.5rem" } }}
    />
  );

interface InvestorMatrixContactGroupCellProps {
  value: string;
  contact: CommunicationMatrixModel | undefined;
}

export const InvestorMatrixContactGroupCell = ({ value, contact }: InvestorMatrixContactGroupCellProps) => {
  // The grouping key drops empty segments, so it is only a fallback when the row model is missing.
  const [fallbackName, , fallbackEmail] = value.split(investorGroupingKeySeparator);
  const contactId = contact?.contactId;
  const contactName = contact ? contact.contactName : (fallbackName ?? "");
  const contactEmail = contact ? contact.contactEmail : fallbackEmail;

  return (
    <Stack sx={{ alignItems: "flex-start" }}>
      {contactId && contactId !== emptyGuid ? (
        <RelatedCommMatrixRecordLink
          recordType="Contact"
          recordId={contactId}
          text={contactName}
          typographyProps={{ variant: "subtitle1", sx: { maxWidth: "11.5rem" } }}
        />
      ) : (
        <Typography variant="subtitle1">{contactName}</Typography>
      )}
      {contactEmail && (
        <TypographyTooltipEllipsis
          text={contactEmail}
          typographyProps={{ variant: "caption", sx: { color: "text.secondary", maxWidth: "11.5rem" } }}
        />
      )}
      {!contactEmail && (
        <NavigateByLink title="Add Email" color="primary" href={`../../contacts/${contactId}/main`} openInNewTab />
      )}
    </Stack>
  );
};

export const FundMatrixContactRowCell = ({ model }: { model: CommunicationMatrixModel }) => (
  <Stack sx={{ pl: 8, alignItems: "flex-start" }}>
    {model.contactId && model.contactId !== emptyGuid ? (
      <RelatedCommMatrixRecordLink recordType="Contact" recordId={model.contactId} text={model.contactName} />
    ) : (
      <TypographyTooltipEllipsis text={model.contactName} />
    )}
    {model.contactEmail && (
      <Typography variant="caption" color="textSecondary">
        {model.contactEmail}
      </Typography>
    )}
    {!model.contactEmail && (
      <NavigateByLink title="Add Email" color="primary" href={`../../contacts/${model.contactId}/main`} openInNewTab />
    )}
  </Stack>
);

export const MatrixFundRowCell = ({ model }: { model: CommunicationMatrixModel }) => {
  const hasFundRecord = Boolean(model.fundName) && Boolean(model.fundId) && model.fundId !== emptyGuid;

  return hasFundRecord ? (
    <RelatedCommMatrixRecordLink recordType="Fund" recordId={model.fundId} text={model.fundName} sx={{ ml: 8 }} />
  ) : (
    <TypographyTooltipEllipsis text={model.fundName} typographyProps={{ sx: { ml: 8 } }} />
  );
};
